import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Field } from '@/components/Field'
import { ProcedureStep } from '@/components/WizardParts'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_CANCEL_COPY } from '@/copy/modals'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type JobRef = components['schemas']['JobRef']
type Ratio = components['schemas']['Ratio']
type BackupJob = components['schemas']['BackupJob']

/**
 * `m-cancel` — process/09-SCREEN-SPECS/m-cancel.md. `jobCancelBackup` is idempotent and returns
 * **accepted, not terminal** (`cancel_requested_at` is the authority; §4c) — `onCancelled` hands
 * the caller the returned `BackupJob` rather than this modal assuming the row is now cancelled
 * (rule 2/M4: no optimistic render).
 */
export function MCancel({
  orgId,
  workspaceId,
  job,
  progress,
  onDismiss,
  onCancelled,
}: {
  orgId: string
  workspaceId: string
  job: JobRef
  progress: Ratio
  onDismiss: () => void
  onCancelled: (job: BackupJob) => void
}) {
  const [reason, setReason] = useState('')

  const cancel = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/backups/{job_id}/cancel', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: job.job_id } },
        body: reason ? { reason } : undefined,
      })
      return unwrap(response as { data?: BackupJob; error?: Problem })
    },
    onSuccess: onCancelled,
  })

  const problem = cancel.error instanceof ApiProblemError ? cancel.error.problem : undefined
  const headingId = 'm-cancel-heading'

  return (
    <Modal dataScr={unitId('m-cancel')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={480}>
      <ModalHeader id={headingId} heading={M_CANCEL_COPY.headingTemplate(job.handle)}>
        {progress.caption}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body mb-6">{problem.title}</p>)}
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7 overflow-hidden">
          <ProcedureStep ordinal="✓" boxed>
            <b>{M_CANCEL_COPY.completedKeptTitle}</b>
            <p className="m-0 text-body text-muted">{M_CANCEL_COPY.completedKeptNote}</p>
          </ProcedureStep>
          <ProcedureStep ordinal="✓" boxed>
            <b>{M_CANCEL_COPY.resumesTitle}</b>
            <p className="m-0 text-body text-muted">{M_CANCEL_COPY.resumesNote}</p>
          </ProcedureStep>
          <ProcedureStep ordinal="–" boxed>
            <b>{M_CANCEL_COPY.closesTitle}</b>
            <p className="m-0 text-body text-muted">{M_CANCEL_COPY.closesNote}</p>
          </ProcedureStep>
        </div>
        <Field label={M_CANCEL_COPY.reasonLabel}>
          <input
            type="text"
            value={reason}
            maxLength={255}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border border-bd2 rounded-1 py-3 px-4 text-body-lg"
          />
        </Field>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_CANCEL_COPY.keepRunningButton}
        </Button>
        <Button data-act={actionId('job.cancel')} danger disabled={cancel.isPending} onClick={() => cancel.mutate()}>
          {M_CANCEL_COPY.cancelButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
